import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { create } from 'zustand'
import type { CabecalhoDePagina } from '@/utils/routeHeader'

/**
 * Título que a TELA dá a si mesma, por cima do que o registro de rotas deriva do pathname.
 *
 * O registro só enxerga o caminho; a Entrada (/chamados/inbox) muda de assunto pela `?pasta=`,
 * e o pedido de 21/10/2026 é que o topo diga a pasta aberta ("Atribuídos a mim", "Meus
 * chamados"). Quem sabe o nome é a tela, então ela publica aqui e o AppLayout lê.
 */
export interface TituloDaTela {
  /** Caminho de quem publicou: título de outra rota nunca vale (ver cabecalhoEfetivo). */
  pathname: string
  titulo: string
  /** Sem a faixa de título/descrição abaixo do topo — a tela já diz onde se está. */
  semFaixa: boolean
}

interface TituloDaTelaStore {
  atual: TituloDaTela | null
}

export const useTituloDaTelaStore = create<TituloDaTelaStore>(() => ({ atual: null }))

/**
 * O que o layout desenha. Na troca de rota o layout renderiza o caminho novo antes de a tela
 * velha desmontar, então o título publicado só vale para o próprio caminho.
 */
export function cabecalhoEfetivo(
  pathname: string,
  base: Pick<CabecalhoDePagina, 'titulo'>,
  tela: TituloDaTela | null,
): { titulo: string; comFaixa: boolean } {
  if (tela && tela.pathname === pathname) return { titulo: tela.titulo, comFaixa: !tela.semFaixa }
  return { titulo: base.titulo, comFaixa: true }
}

/** Publica o título da tela enquanto ela estiver montada. `null` não publica. */
export function useTituloDaTela(titulo: string | null, { semFaixa = false } = {}) {
  const { pathname } = useLocation()
  useEffect(() => {
    if (titulo == null) return
    const meu: TituloDaTela = { pathname, titulo, semFaixa }
    useTituloDaTelaStore.setState({ atual: meu })
    return () => {
      // Só limpa o que é seu: a tela seguinte pode já ter publicado.
      if (useTituloDaTelaStore.getState().atual === meu) useTituloDaTelaStore.setState({ atual: null })
    }
  }, [pathname, titulo, semFaixa])
}
